import { PlanSelector } from "@client/components/PlanSelector";
import { StripeCheckoutSuccess } from "@client/components/StripeCheckoutSuccess";
import { PageContainer } from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { Progress } from "@client/components/ui/progress";
import { useOnboarding } from "@client/hooks/useOnboarding";
import { useUserLimits } from "@client/hooks/useUserLimits";
import { authClient } from "@client/lib/auth-client";
import { trpc } from "@client/lib/trpc-client";
import { BillingStatusCard } from "@client/routes/(protected)/-components/billing-status-card";
import { RecentJobsCard } from "@client/routes/(protected)/-components/recent-jobs-card";
import { RecentSitesCard } from "@client/routes/(protected)/-components/recent-sites-card";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import {
	Check,
	Clock,
	Copy,
	FileText,
	Gift,
	Globe,
	Plus,
	Settings,
	Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

// Validate search params - coerce to handle both string and boolean
const dashboardSearchSchema = z.object({
	success: z.coerce.string().optional(),
	canceled: z.coerce.string().optional(),
	session_id: z.string().optional(),
});

export const Route = createFileRoute("/(protected)/dashboard")({
	component: DashboardPage,
	validateSearch: (search) => dashboardSearchSchema.parse(search),
});

function DashboardPage() {
	const { atSiteLimit, siteCount, planSiteLimit, userPlan } = useUserLimits();
	const sitesQuery = trpc.sites.getRecent.useQuery({ limit: 5 });
	const { data: creditStats, refetch: refetchCredits } =
		trpc.credits.getBalance.useQuery();
	const activitiesQuery = trpc.sites.getRecentActivities.useQuery({
		limit: 10,
	});
	const { needsOnboarding, isLoading: onboardingLoading } = useOnboarding();
	const [showPlanSelector, setShowPlanSelector] = useState(false);
	const { data: session } = authClient.useSession();

	// Get search params for checkout success handling
	const search = useSearch({ from: "/(protected)/dashboard" });
	const isCheckoutSuccess = search.success === "true" && !!search.session_id;

	// Fire X Ads Lead event once for new signups (covers OAuth + any path not tracked at signup)
	// Uses email as conversion_id so X Ads can deduplicate against the password-signup event
	useEffect(() => {
		const user = session?.user;
		if (!user?.id || !user.email) return;
		const trackingKey = `signup_tracked_${user.id}`;
		if (sessionStorage.getItem(trackingKey)) return;
		const createdAt = user.createdAt ? new Date(user.createdAt).getTime() : 0;
		const isNewSignup = Date.now() - createdAt < 2 * 60 * 1000; // within 2 minutes
		if (isNewSignup) {
			sessionStorage.setItem(trackingKey, "1");
			window.twq?.("event", "tw-rccfj-rciv1", {
				conversion_id: user.email,
				email_address: user.email,
			});
		}
	}, [session?.user?.id, session?.user]);

	// Show plan selector for new users
	useEffect(() => {
		if (needsOnboarding && !onboardingLoading) {
			setShowPlanSelector(true);
		}
	}, [needsOnboarding, onboardingLoading]);

	const handleOnboardingComplete = () => {
		setShowPlanSelector(false);
		sitesQuery.refetch();
	};

	// Handle checkout success completion - refetch credits
	const handleCheckoutComplete = () => {
		refetchCredits();
	};

	// Calculate stats
	const totalSites = siteCount;
	const totalFiles =
		sitesQuery.data?.sites?.filter((site) => site.status === "active").length ??
		0;
	const activeJobs =
		activitiesQuery.data?.filter(
			(activity) =>
				activity.status === "processing" || activity.status === "queued",
		).length ?? 0;
	const creditsAvailable = creditStats?.balance?.balance ?? 0;

	// Show checkout success page if returning from Stripe
	if (isCheckoutSuccess) {
		return (
			<PageContainer maxWidth="full" className="relative">
				<div className="flex min-h-[60vh] items-center justify-center">
					<StripeCheckoutSuccess onComplete={handleCheckoutComplete} />
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer maxWidth="full" className="relative">
			{/* Plan Selector Modal for new users */}
			<PlanSelector
				open={showPlanSelector}
				onOpenChange={setShowPlanSelector}
				onComplete={handleOnboardingComplete}
			/>

			{/* Page Header */}
			<div className="mb-8">
				<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div>
						<h1 className="font-semibold text-3xl tracking-tight">Dashboard</h1>
						<p className="mt-1 text-muted-foreground">
							Manage your LLMs.txt files and monitor generation progress
						</p>
					</div>
					<div className="flex gap-3">
						<Button asChild variant="outline" size="sm">
							<Link to="/settings">
								<Settings className="mr-2 h-4 w-4" />
								Settings
							</Link>
						</Button>
						<Button asChild size="sm">
							<Link to="/sites/new">
								<Plus className="mr-2 h-4 w-4" />
								New Site
							</Link>
						</Button>
					</div>
				</div>
			</div>

			{/* Stats Grid */}
			<div className="mb-8 grid gap-4 md:grid-cols-4">
				<StatCard
					title="Sites"
					value={totalSites}
					icon={Globe}
					description={`${planSiteLimit - totalSites} slots remaining`}
					progress={(totalSites / planSiteLimit) * 100}
					warning={atSiteLimit}
				/>
				<StatCard
					title="Active Files"
					value={totalFiles}
					icon={FileText}
					description="LLMs.txt files generated"
				/>
				<StatCard
					title="Active Jobs"
					value={activeJobs}
					icon={Clock}
					description={activeJobs > 0 ? "Processing..." : "No active jobs"}
					isProcessing={activeJobs > 0}
				/>
				<StatCard
					title="Credits"
					value={creditsAvailable}
					icon={Zap}
					description="Available balance"
					warning={creditsAvailable < 100}
				/>
			</div>

			{/* Content Grid */}
			<div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
				<div className="space-y-6">
					<RecentSitesCard
						sitesQuery={sitesQuery}
						atSiteLimit={atSiteLimit}
						siteCount={siteCount}
						planSiteLimit={planSiteLimit}
					/>
				</div>

				<div className="space-y-6">
					<RecentJobsCard />
					<BillingStatusCard
						userPlan={userPlan}
						atSiteLimit={atSiteLimit}
						siteCount={siteCount}
						planSiteLimit={planSiteLimit}
					/>
				</div>
			</div>

			{/* Full Width Referral CTA */}
			<div className="w-full pb-8">
				<ReferralDashboardCard />
			</div>
		</PageContainer>
	);
}

// Dashboard Referral CTA
function ReferralDashboardCard() {
	const profile = trpc.user.getProfile.useQuery();
	const [copied, setCopied] = useState(false);

	const code = profile.data?.referralCode;
	const baseUrl = import.meta.env.PROD
		? "https://app.llmgenerator.com"
		: import.meta.env.VITE_FRONTEND_URL || window.location.origin;
	const referralLink = code ? `${baseUrl}/?ref=${code}` : "";

	const handleCopy = async () => {
		if (!referralLink) return;

		try {
			await navigator.clipboard.writeText(referralLink);
			setCopied(true);
			toast.success("Referral link copied!");
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Failed to copy link");
		}
	};

	return (
		<Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent dark:border-primary/30">
			<CardHeader className="pb-3">
				<CardTitle className="flex items-center gap-2 text-lg">
					<Gift className="h-5 w-5 text-primary" />
					Share & Earn 100 Credits
				</CardTitle>
			</CardHeader>
			<CardContent>
				<p className="mb-4 text-muted-foreground text-sm">
					Invite friends to LLM Generator. When they sign up using your link and
					<strong> purchase credits or subscribe to a plan</strong>, you both
					receive 100 bonus credits!
				</p>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
					<div className="flex flex-1 items-center gap-2">
						<Input
							readOnly
							value={referralLink || "Loading..."}
							className="bg-muted/50 font-mono text-sm dark:bg-gray-800"
							onClick={(e) => (e.target as HTMLInputElement).select()}
						/>
						<Button
							variant="secondary"
							size="icon"
							onClick={handleCopy}
							className="shrink-0"
						>
							{copied ? (
								<Check className="h-4 w-4 text-green-500" />
							) : (
								<Copy className="h-4 w-4" />
							)}
						</Button>
					</div>
					<Button
						asChild
						variant="outline"
						className="w-full shrink-0 sm:w-auto"
					>
						<Link to="/settings/referrals">View Stats</Link>
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

// Clean stat card component
function StatCard({
	title,
	value,
	icon: Icon,
	description,
	progress,
	warning,
	isProcessing,
}: {
	title: string;
	value: string | number;
	icon: React.ComponentType<{ className?: string }>;
	description?: string;
	progress?: number;
	warning?: boolean;
	isProcessing?: boolean;
}) {
	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
				<CardTitle className="font-medium text-muted-foreground text-sm">
					{title}
				</CardTitle>
				<Icon
					className={`h-4 w-4 ${warning ? "text-amber-500" : isProcessing ? "animate-pulse text-blue-500" : "text-muted-foreground"}`}
				/>
			</CardHeader>
			<CardContent>
				<div
					className={`font-semibold text-2xl ${warning ? "text-amber-500" : ""}`}
				>
					{value}
				</div>
				{description && (
					<p className="mt-1 text-muted-foreground text-xs">{description}</p>
				)}
				{progress !== undefined && (
					<div className="mt-3">
						<Progress value={progress} className="h-1.5" />
					</div>
				)}
			</CardContent>
		</Card>
	);
}
